import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Plus, X, Upload, FileText, Image as ImageIcon, Presentation, Layers3,
  Check, Send, MessageSquare, ChevronRight, Rocket, CircleDot, ShieldCheck, CalendarClock,
} from "lucide-react";
import PdfAnnotator from "@/components/annotator/PdfAnnotator";
import ImageAnnotator from "@/components/annotator/ImageAnnotator";
import TimelinePanel from "@/components/ops/TimelinePanel";
import { uploadMany, inferKindFromFile, kindLabel, fileUrl } from "@/lib/uploads";

const KIND_ORDER = ["quotation_pdf", "twod", "threed", "ppt"];
const KIND_ICONS = { quotation_pdf: FileText, twod: ImageIcon, threed: Layers3, ppt: Presentation };

export default function OpsQuotationsPage() {
  const { isAdmin, roleName, roleType, principal } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null); // full detail with annotations

  const isOps = isAdmin || ["Operations Manager", "Operations Executive"].includes(roleName);
  const isCrm = isAdmin || ["CRM Manager", "CRM Executive"].includes(roleName);
  const isDesigner = roleName === "Interior Designer";
  const isDirector = isAdmin || roleType === "director";

  const load = useCallback(async () => {
    try {
      const [q, p] = await Promise.all([
        api.get("/ops/quotations"),
        api.get("/design/projects").catch(() => ({ data: [] })),
      ]);
      setQuotes(q.data);
      setProjects(p.data);
      if (!selectedId && q.data.length > 0) setSelectedId(q.data[0].id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, [selectedId]);

  const loadSelected = useCallback(async (id) => {
    if (!id) { setSelected(null); return; }
    try {
      const { data } = await api.get(`/ops/quotations/${id}`);
      setSelected(data);
    } catch (e) {
      setSelected(null);
    }
  }, []);

  useEffect(() => { load(); }, []);
  useEffect(() => { loadSelected(selectedId); }, [selectedId, loadSelected]);

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === selected?.project_id),
    [projects, selected]
  );

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-6 flex items-center justify-between bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Operations · Quotations</p>
          <h1 className="font-display text-3xl text-white mt-1">Quotation & Timeline Console</h1>
        </div>
        {isOps && (
          <button className="iz-btn-primary flex items-center gap-2" onClick={() => setShowCreate(true)} data-testid="new-quote-btn">
            <Upload className="w-4 h-4" /> Upload New Quotation
          </button>
        )}
      </div>

      <div className="px-10 py-8 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 items-start">
        {/* List sidebar */}
        <div className="space-y-2">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-2">All Quotations</p>
          {quotes.length === 0 ? (
            <div className="iz-card p-6 text-center">
              <p className="text-xs font-mono text-gray-500 mb-3">No quotations yet.</p>
              {isOps && (
                <button onClick={() => setShowCreate(true)} className="iz-btn-secondary text-xs" data-testid="empty-upload">
                  Upload your first
                </button>
              )}
            </div>
          ) : (
            quotes.map((q) => {
              const d = (q.designer_review || {}).status;
              const dir = (q.director_review || {}).status;
              const statusColor =
                q.sent_at ? "text-emerald-400"
                : q.final_approval_requested ? "text-[#d4af37]"
                : (d === "changes_requested" || dir === "changes_requested") ? "text-amber-400"
                : "text-gray-500";
              const statusText =
                q.sent_at ? "Sent" :
                q.final_approval_requested ? (d === "approved" && dir === "approved" ? "Ready to send" : "Final review") :
                (d === "changes_requested" || dir === "changes_requested") ? "Changes requested" :
                (d === "approved" && dir === "approved") ? "Approved · request final" :
                "In review";
              return (
                <button
                  key={q.id}
                  onClick={() => setSelectedId(q.id)}
                  className={`iz-card p-3 w-full text-left hover:border-[#d4af37]/40 transition-colors ${selectedId === q.id ? "border-[#d4af37]" : ""}`}
                  data-testid={`quote-row-${q.id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-white text-sm font-medium truncate">{q.client_name}{q.city ? ` · ${q.city}` : ""}</p>
                    <span className="text-[10px] font-mono text-gray-500 ml-2 shrink-0">v{q.current_version}</span>
                  </div>
                  <p className="text-[11px] text-gray-500 truncate">{q.project_name}</p>
                  <p className={`text-[10px] font-mono uppercase tracking-widest mt-2 ${statusColor}`}>{statusText}</p>
                </button>
              );
            })
          )}
        </div>

        {/* Detail view */}
        <div className="min-w-0">
          {selected ? (
            <QuotationDetail
              quote={selected}
              project={selectedProject}
              isOps={isOps}
              isCrm={isCrm}
              isDesigner={isDesigner && selected.designer_id === principal?.id}
              isDirector={isDirector}
              onRefresh={() => { load(); loadSelected(selectedId); }}
            />
          ) : (
            <div className="iz-card p-12 text-center">
              <FileText className="w-8 h-8 text-gray-700 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Select a quotation to review or upload a new one.</p>
            </div>
          )}
        </div>
      </div>

      <UploadModal
        open={showCreate}
        projects={projects}
        mode="create"
        onClose={() => setShowCreate(false)}
        onDone={() => { setShowCreate(false); load(); }}
      />
    </div>
  );
}

function QuotationDetail({ quote, project, isOps, isCrm, isDesigner, isDirector, onRefresh }) {
  const [tab, setTab] = useState("files"); // files | timeline
  const [showNewVersion, setShowNewVersion] = useState(false);
  const currentVersion = quote.versions?.find((v) => v.version === quote.current_version);
  const currentAnnotations = useMemo(
    () => (quote.annotations || []).filter((a) => a.version === quote.current_version),
    [quote.annotations, quote.current_version],
  );
  const currentFilesFlat = useMemo(() => {
    if (!currentVersion) return [];
    const out = [];
    KIND_ORDER.forEach((k) => (currentVersion.files?.[k] || []).forEach((f) => out.push({ ...f, kind: k })));
    return out;
  }, [currentVersion]);

  const [activeFile, setActiveFile] = useState(null);
  useEffect(() => {
    // default to first pdf if available
    const pdf = currentFilesFlat.find((f) => f.kind === "quotation_pdf");
    setActiveFile(pdf || currentFilesFlat[0] || null);
  }, [quote.id, quote.current_version]);

  const designerStatus = (quote.designer_review || {}).status || "pending";
  const directorStatus = (quote.director_review || {}).status || "pending";
  const bothApproved = designerStatus === "approved" && directorStatus === "approved";

  const doReview = async (status) => {
    try {
      await api.post(`/ops/quotations/${quote.id}/review`, { status });
      toast.success(status === "approved" ? "Approved" : "Changes requested");
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const doRequestFinal = async () => {
    try {
      await api.post(`/ops/quotations/${quote.id}/request-final-approval`, {});
      toast.success("Final approval requested");
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const doSend = async () => {
    try {
      await api.post(`/ops/quotations/${quote.id}/send-to-client`);
      toast.success("Sent to client");
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addAnnotation = async ({ x, y, w, h, remark, page }) => {
    if (!activeFile) return;
    try {
      await api.post(`/ops/quotations/${quote.id}/annotations`, {
        version: quote.current_version,
        file_kind: activeFile.kind,
        file_id: activeFile.file_id,
        page,
        x, y, w, h,
        remark,
      });
      toast.success("Remark saved");
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const resolveAnnotation = async (id) => {
    try {
      await api.patch(`/ops/quotations/${quote.id}/annotations/${id}`, { status: "resolved" });
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const canAnnotate = (isDesigner || isDirector) && !quote.sent_at;
  const canRequestFinal = isOps && bothApproved && !quote.final_approval_requested && !quote.sent_at;
  const canSend = isOps && quote.final_approval_requested && bothApproved && !quote.sent_at;
  const canUploadNewVersion = isOps && !quote.sent_at;

  const fileAnnotations = (quote.annotations || []).filter(
    (a) => a.version === quote.current_version && a.file_id === activeFile?.file_id
  );

  return (
    <div className="space-y-4">
      {/* Header card */}
      <div className="iz-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Quotation · v{quote.current_version}</p>
            <h2 className="font-display text-2xl text-white mt-1">{quote.client_name}{quote.city ? ` · ${quote.city}` : ""}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{quote.project_name}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill role="Designer" status={designerStatus} />
            <StatusPill role="Director" status={directorStatus} />
            {quote.sent_at && <span className="text-[10px] font-mono uppercase tracking-widest bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-2 py-1 rounded">Sent</span>}
          </div>
        </div>

        {/* action bar */}
        <div className="flex items-center gap-2 flex-wrap mt-5 pt-5 border-t border-[#1a1a1a]">
          {canAnnotate && (
            <>
              <button onClick={() => doReview("approved")} className="iz-btn-secondary text-xs flex items-center gap-1.5 hover:border-emerald-500 hover:text-emerald-400" data-testid="review-approve">
                <Check className="w-3.5 h-3.5" /> Approve current version
              </button>
              <button onClick={() => doReview("changes_requested")} className="iz-btn-secondary text-xs flex items-center gap-1.5 hover:border-amber-500 hover:text-amber-400" data-testid="review-changes">
                <MessageSquare className="w-3.5 h-3.5" /> Request changes
              </button>
            </>
          )}
          {canUploadNewVersion && (
            <button onClick={() => setShowNewVersion(true)} className="iz-btn-secondary text-xs flex items-center gap-1.5" data-testid="new-version">
              <Upload className="w-3.5 h-3.5" /> Upload new version
            </button>
          )}
          {canRequestFinal && (
            <button onClick={doRequestFinal} className="iz-btn-primary text-xs flex items-center gap-1.5" data-testid="request-final">
              <ShieldCheck className="w-3.5 h-3.5" /> Request final approval
            </button>
          )}
          {canSend && (
            <button onClick={doSend} className="iz-btn-primary text-xs flex items-center gap-1.5" data-testid="send-client">
              <Send className="w-3.5 h-3.5" /> Send to client
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#1a1a1a] gap-6">
        <TabButton active={tab === "files"} onClick={() => setTab("files")} testid="tab-files">
          Files & Review
        </TabButton>
        <TabButton active={tab === "timeline"} onClick={() => setTab("timeline")} testid="tab-timeline">
          Production Timeline
        </TabButton>
      </div>

      {tab === "files" && (
        <div className="grid grid-cols-1 xl:grid-cols-[220px_1fr_280px] gap-4">
          {/* File list */}
          <div className="space-y-1.5">
            {KIND_ORDER.map((k) => {
              const files = currentVersion?.files?.[k] || [];
              if (files.length === 0) return null;
              const Icon = KIND_ICONS[k];
              return (
                <div key={k}>
                  <p className="text-[9px] font-mono uppercase tracking-[0.2em] text-gray-500 px-2 mt-3 mb-1">{kindLabel(k)}</p>
                  {files.map((f) => (
                    <button
                      key={f.file_id}
                      onClick={() => setActiveFile({ ...f, kind: k })}
                      className={`w-full text-left px-3 py-2 rounded-md border transition-colors flex items-center gap-2 ${activeFile?.file_id === f.file_id ? "border-[#d4af37] bg-[#d4af37]/5" : "border-transparent hover:border-[#262626] hover:bg-[#101010]"}`}
                      data-testid={`file-${f.file_id}`}
                    >
                      <Icon className="w-3.5 h-3.5 text-[#d4af37] shrink-0" />
                      <span className="text-xs text-gray-200 truncate">{f.original_filename}</span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>

          {/* Viewer */}
          <div className="iz-card overflow-hidden min-h-[600px] flex flex-col">
            {!activeFile && <p className="text-xs font-mono text-gray-600 p-10 text-center">No files.</p>}
            {activeFile && activeFile.kind === "quotation_pdf" && (
              <PdfAnnotator
                fileId={activeFile.file_id}
                annotations={fileAnnotations}
                canDraw={canAnnotate}
                onCreate={addAnnotation}
                onResolve={resolveAnnotation}
              />
            )}
            {activeFile && (activeFile.kind === "twod" || activeFile.kind === "threed") && (
              <ImageAnnotator
                fileId={activeFile.file_id}
                annotations={fileAnnotations}
                canDraw={canAnnotate}
                onCreate={addAnnotation}
                onResolve={resolveAnnotation}
              />
            )}
            {activeFile && activeFile.kind === "ppt" && (
              <PptFallback fileId={activeFile.file_id} filename={activeFile.original_filename} />
            )}
          </div>

          {/* Remarks sidebar */}
          <div className="iz-card p-4">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-3">Remarks · v{quote.current_version}</p>
            {currentAnnotations.length === 0 && (
              <p className="text-xs text-gray-600 italic">No remarks yet. Reviewers drag on the drawing to add one.</p>
            )}
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
              {currentAnnotations.map((a) => (
                  <div key={a.id} className={`border-l-2 pl-3 py-1.5 ${a.status === "resolved" ? "border-emerald-500/50 opacity-60" : "border-[#d4af37]"}`} data-testid={`remark-${a.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37]">
                        {a.author_role}{a.page ? ` · pg ${a.page}` : ""}
                      </p>
                      {a.status !== "resolved" && isOps && (
                        <button
                          onClick={() => resolveAnnotation(a.id)}
                          className="text-[9px] font-mono uppercase tracking-widest text-emerald-400 hover:text-emerald-300"
                        >
                          Resolve
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-200 leading-relaxed">{a.remark}</p>
                    <p className="text-[9px] text-gray-500 font-mono mt-1">{a.author_name} · {new Date(a.created_at).toLocaleString()}</p>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {tab === "timeline" && (
        <TimelinePanel
          projectId={quote.project_id}
          project={project}
          canEdit={isOps || isCrm || isDirector}
          canCompute={isOps || isDirector}
        />
      )}

      <UploadModal
        open={showNewVersion}
        projects={[]}
        mode="version"
        quotationId={quote.id}
        onClose={() => setShowNewVersion(false)}
        onDone={() => { setShowNewVersion(false); onRefresh(); }}
      />
    </div>
  );
}

function StatusPill({ role, status }) {
  const map = {
    pending: { color: "text-gray-500 border-[#262626] bg-[#0a0a0a]", label: "Pending" },
    changes_requested: { color: "text-amber-400 border-amber-500/40 bg-amber-500/5", label: "Changes" },
    approved: { color: "text-emerald-400 border-emerald-500/40 bg-emerald-500/5", label: "Approved" },
  };
  const m = map[status] || map.pending;
  return (
    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border ${m.color}`} data-testid={`pill-${role.toLowerCase()}`}>
      {role} · {m.label}
    </span>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      className={`pb-2.5 text-sm transition-colors border-b-2 ${active ? "text-[#d4af37] border-[#d4af37]" : "text-gray-500 border-transparent hover:text-gray-300"}`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function PptFallback({ fileId, filename }) {
  return (
    <div className="flex-1 flex items-center justify-center p-10">
      <div className="text-center max-w-sm">
        <Presentation className="w-8 h-8 text-[#d4af37] mx-auto mb-3" />
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-2">Deck</p>
        <p className="text-sm text-white mb-2">{filename}</p>
        <p className="text-xs text-gray-500 mb-4">PPT annotations aren&apos;t supported in-browser. Download the deck, add your notes as remarks in the sidebar.</p>
        <a
          href={fileUrl(fileId)}
          target="_blank" rel="noreferrer"
          className="iz-btn-secondary inline-flex items-center gap-1.5 text-xs"
          data-testid="download-ppt"
        >
          <ChevronRight className="w-3 h-3" /> Download deck
        </a>
      </div>
    </div>
  );
}

function UploadModal({ open, projects, mode, quotationId, onClose, onDone }) {
  const [projectId, setProjectId] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]); // [{file, kind}]
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) { setProjectId(""); setNotes(""); setItems([]); setErr(""); setProgress(0); }
  }, [open]);

  const addFiles = (fileList) => {
    const arr = Array.from(fileList).map((f) => ({ file: f, kind: inferKindFromFile(f) }));
    setItems((prev) => [...prev, ...arr]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (mode === "create" && !projectId) { setErr("Select a project"); return; }
    if (items.length === 0) { setErr("Add at least one file"); return; }
    if (mode === "create" && !items.some((it) => it.kind === "quotation_pdf")) {
      setErr("A quotation PDF is required for the first upload"); return;
    }
    setUploading(true);
    try {
      const uploaded = await uploadMany(items.map((it) => it.file), setProgress);
      const file_ids = uploaded.map((u) => u.id);
      const kinds = {};
      uploaded.forEach((u, idx) => { kinds[u.id] = items[idx].kind; });
      if (mode === "create") {
        await api.post("/ops/quotations", { project_id: projectId, notes, file_ids, kinds });
        toast.success("Quotation uploaded");
      } else {
        await api.post(`/ops/quotations/${quotationId}/new-version`, { notes, file_ids, kinds });
        toast.success("New version uploaded");
      }
      onDone();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="upload-modal">
      <div className="w-full max-w-3xl iz-card p-8 relative max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500">
          <X className="w-4 h-4" />
        </button>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-2">
          {mode === "create" ? "Upload" : "New version"}
        </p>
        <h2 className="font-display text-2xl text-white mb-6">
          {mode === "create" ? "Upload Quotation Package" : "Upload New Revision"}
        </h2>

        <form onSubmit={submit} className="space-y-5">
          {mode === "create" && (
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Project</label>
              <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="iz-input" required data-testid="upload-project">
                <option value="">Select project…</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.project_name} · {p.client_name}</option>)}
              </select>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] font-mono uppercase tracking-widest text-gray-400">Files</label>
              <button type="button" onClick={() => inputRef.current?.click()} className="iz-btn-secondary text-xs flex items-center gap-1" data-testid="add-files">
                <Plus className="w-3 h-3" /> Add files
              </button>
              <input ref={inputRef} type="file" multiple hidden accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.ppt,.pptx"
                     onChange={(e) => addFiles(e.target.files)} data-testid="file-input"/>
            </div>

            <div
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
              className="border border-dashed border-[#262626] rounded-md p-4 min-h-[80px]"
            >
              {items.length === 0 ? (
                <p className="text-xs font-mono text-gray-600 text-center py-4">
                  Drop PDF (quotation), 2D &amp; 3D images, PPT here — or click &ldquo;Add files&rdquo;.
                </p>
              ) : (
                <ul className="space-y-2">
                  {items.map((it, i) => (
                    <li key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`file-item-${i}`}>
                      <div className="col-span-6 text-sm text-white truncate">{it.file.name}</div>
                      <div className="col-span-2 text-[10px] font-mono text-gray-500 text-right">
                        {(it.file.size / 1024).toFixed(0)} KB
                      </div>
                      <select
                        className="iz-input col-span-3 !py-1 !text-xs"
                        value={it.kind}
                        onChange={(e) => setItems(items.map((x, idx) => idx === i ? { ...x, kind: e.target.value } : x))}
                        data-testid={`file-kind-${i}`}
                      >
                        <option value="quotation_pdf">Quotation PDF</option>
                        <option value="twod">2D Drawing</option>
                        <option value="threed">3D Render</option>
                        <option value="ppt">PPT / Deck</option>
                      </select>
                      <button type="button" onClick={() => setItems(items.filter((_, idx) => idx !== i))} className="col-span-1 iz-btn-secondary p-1.5">
                        <X className="w-3 h-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Notes</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="iz-input" placeholder="Optional revision notes…" />
          </div>

          {uploading && (
            <div className="w-full h-1.5 bg-[#111] rounded overflow-hidden">
              <div className="h-full bg-[#d4af37]" style={{ width: `${progress}%` }} />
            </div>
          )}
          {err && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded-md px-3 py-2">{err}</div>}

          <div className="flex justify-end gap-3 pt-4 border-t border-[#1a1a1a]">
            <button type="button" onClick={onClose} className="iz-btn-secondary">Cancel</button>
            <button type="submit" disabled={uploading} className="iz-btn-primary" data-testid="upload-submit">
              {uploading ? "Uploading…" : mode === "create" ? "Upload package" : "Upload revision"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
